// K11 (Finar 4.6) — KPI dipnot numaralandırması: grid, DOCX ve Excel AYNI diziyi kullanır.
// Sıra deterministik: gösterge sırası (çağıranın verdiği grid sırası) → önce göstergenin kendi
// dipnotu, sonra o göstergenin hücre dipnotları (yıl artan, ardından işletme sırası). Boş/yalnız
// boşluk dipnot sayılmaz; bir dipnot silinince sonrakiler kayar (numara kalıcı kimlik DEĞİL).
// Donmuş belgeler kendi manifestinden yeniden numaralanır — numaralar render anında üretilir.

export interface KpiFootnoteCellInput {
  footnote?: string | null | undefined;
}

export interface KpiFootnoteIndicatorInput {
  id: string;
  label: string;
  footnote?: string | null | undefined;
  cells: Record<string, Record<number, KpiFootnoteCellInput | undefined> | undefined>;
}

export interface KpiFootnoteEntityInput {
  id: string;
  name: string;
}

export interface KpiFootnoteEntry {
  n: number;
  /** `ind:<indicatorId>` ya da `cell:<indicatorId>:<entityId>:<year>` */
  key: string;
  kind: "indicator" | "cell";
  label: string;
  /** yalnız hücre dipnotunda: "2025" (tek işletme) ya da "2025 · <işletme>" */
  where?: string;
  text: string;
}

export interface KpiFootnoteNumbering {
  refs: Map<string, number>;
  list: KpiFootnoteEntry[];
}

export function indicatorFootnoteKey(indicatorId: string): string {
  return `ind:${indicatorId}`;
}

export function cellFootnoteKey(indicatorId: string, entityId: string, year: number): string {
  return `cell:${indicatorId}:${entityId}:${String(year)}`;
}

/** Liste satırı metni (kanallar arası ortak): "Etiket (2025): metin" / "Etiket: metin". */
export function footnoteLineText(e: KpiFootnoteEntry): string {
  return e.where ? `${e.label} (${e.where}): ${e.text}` : `${e.label}: ${e.text}`;
}

export function numberFootnotes(
  indicators: readonly KpiFootnoteIndicatorInput[],
  years: readonly number[],
  entities: readonly KpiFootnoteEntityInput[],
): KpiFootnoteNumbering {
  const refs = new Map<string, number>();
  const list: KpiFootnoteEntry[] = [];
  const sortedYears = [...new Set(years)].sort((a, b) => a - b);
  const multiEntity = entities.length > 1;
  const push = (key: string, entry: Omit<KpiFootnoteEntry, "n" | "key">): void => {
    if (refs.has(key)) return;
    const n = list.length + 1;
    refs.set(key, n);
    list.push({ n, key, ...entry });
  };
  for (const ind of indicators) {
    const own = ind.footnote?.trim();
    if (own) push(indicatorFootnoteKey(ind.id), { kind: "indicator", label: ind.label, text: own });
    for (const year of sortedYears) {
      for (const ent of entities) {
        const text = ind.cells[ent.id]?.[year]?.footnote?.trim();
        if (!text) continue;
        const where = multiEntity ? `${String(year)} · ${ent.name}` : String(year);
        push(cellFootnoteKey(ind.id, ent.id, year), {
          kind: "cell",
          label: ind.label,
          where,
          text,
        });
      }
    }
  }
  return { refs, list };
}

/**
 * TEK giriş noktası (üç kanal — grid, DOCX, Excel): numaralama yalnız GÖRÜNÜR yıllar üzerinden.
 * Excel'in grid'i gizli ÖNCEKİ_YIL penceresini (min-1) de taşır; o yıl `hiddenYear` ile dışlanır —
 * aksi halde gizli kolondaki hücre dipnotu numara alır ve sonraki tüm numaraları kaydırırdı.
 */
export function numberGridFootnotes(
  grid: {
    indicators: readonly KpiFootnoteIndicatorInput[];
    years: readonly number[];
    entities: readonly KpiFootnoteEntityInput[];
  },
  opts: { hiddenYear?: number | undefined } = {},
): KpiFootnoteNumbering {
  const years = grid.years.filter((y) => y !== opts.hiddenYear);
  return numberFootnotes(grid.indicators, years, grid.entities);
}
